import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import ListPaginationBar from "../components/ListPaginationBar";
import {
  parsePaginatedResponse,
  REFERENCE_DATA_PAGE_SIZE,
} from "../constants/pagination";
import { departmentsAPI, userProfilesAPI } from "../services/api";
import { useRequirePermission } from "../hooks/useHasPermission";
import { PERMISSIONS } from "../utils/permissions";

const PERMISSION_FIELDS = [
  { key: "can_create_ticket", labelKey: "referencePages.permissions.canCreateTicket" },
  { key: "can_close_ticket", labelKey: "referencePages.permissions.canCloseTicket" },
  { key: "can_manage_customers", labelKey: "referencePages.permissions.canManageCustomers" },
  { key: "can_manage_contact_reasons", labelKey: "referencePages.permissions.canManageContactReasons" },
  { key: "can_manage_communication_methods", labelKey: "referencePages.permissions.canManageCommunicationMethods" },
  { key: "can_manage_short_subjects", labelKey: "referencePages.permissions.canManageShortSubjects" },
  { key: "can_manage_branches", labelKey: "referencePages.permissions.canManageBranches" },
  { key: "can_manage_products", labelKey: "referencePages.permissions.canManageProducts" },
  { key: "can_manage_departments", labelKey: "referencePages.permissions.canManageDepartments" },
  { key: "can_manage_users", labelKey: "referencePages.permissions.canManageUsers" },
  { key: "can_view_reports", labelKey: "referencePages.permissions.canViewReports" },
  { key: "can_download_attachments", labelKey: "referencePages.permissions.canDownloadAttachments" },
  { key: "can_reply_tickets", labelKey: "referencePages.permissions.canReplyTickets" },
  { key: "can_classify_tickets", labelKey: "referencePages.permissions.canClassifyTickets" },
  { key: "can_edit_ticket", labelKey: "referencePages.permissions.canEditTicket" },
  { key: "can_view_activity_log", labelKey: "referencePages.permissions.canViewActivityLog" },
  { key: "can_delete_reply", labelKey: "referencePages.permissions.canDeleteReply" },
  { key: "can_export_tickets", labelKey: "referencePages.permissions.canExportTickets" },
  { key: "can_manage_action_types", labelKey: "referencePages.permissions.canManageActionTypes" },
  { key: "can_manage_employees", labelKey: "referencePages.permissions.canManageEmployees" },
  { key: "can_add_reply_action", labelKey: "referencePages.permissions.canAddReplyAction" },
  { key: "can_delete_reply_action", labelKey: "referencePages.permissions.canDeleteReplyAction" },
  { key: "can_view_actions_report", labelKey: "referencePages.permissions.canViewActionsReport" },
  { key: "can_export_actions_report", labelKey: "referencePages.permissions.canExportActionsReport" },
];

const emptyForm = {
  username: "",
  password: "",
  email: "",
  first_name: "",
  last_name: "",
  arabic_name: "",
  english_name: "",
  job_title: "",
  mobile: "",
  departments: [],
  can_read_all_tickets: false,
  can_create_ticket: false,
  can_close_ticket: false,
  can_manage_customers: false,
  can_manage_contact_reasons: false,
  can_manage_communication_methods: false,
  can_manage_short_subjects: false,
  can_manage_branches: false,
  can_manage_products: false,
  can_manage_departments: false,
  can_manage_users: false,
  can_view_reports: false,
  can_download_attachments: false,
  can_reply_tickets: false,
  can_classify_tickets: false,
  can_edit_ticket: false,
  can_view_activity_log: false,
  can_delete_reply: false,
  can_export_tickets: false,
  can_manage_action_types: false,
  can_manage_employees: false,
  can_add_reply_action: false,
  can_delete_reply_action: false,
  can_view_actions_report: false,
  can_export_actions_report: false,
  is_active: true,
  receive_email: true,
};

const UsersPage = ({ onNavigate }) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === "ar";
  const hasAccess = useRequirePermission(PERMISSIONS.MANAGE_USERS, onNavigate);

  const [users, setUsers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(REFERENCE_DATA_PAGE_SIZE);
  const [totalCount, setTotalCount] = useState(0);
  const [tableFilter, setTableFilter] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editWithoutCreateConfirmOpen, setEditWithoutCreateConfirmOpen] =
    useState(false);
  const [initialPermissionSnapshot, setInitialPermissionSnapshot] = useState(null);

  const loadDepartments = useCallback(async () => {
    try {
      const deptRes = await departmentsAPI.list({
        page_size: REFERENCE_DATA_PAGE_SIZE,
      });
      const { items } = parsePaginatedResponse(deptRes);
      setDepartments(items);
    } catch (err) {
      console.error("Error loading departments:", err);
    }
  }, []);

  const loadUsers = useCallback(
    async (overrides = {}) => {
      const currentPage = overrides.page ?? page;
      const currentSize = overrides.page_size ?? pageSize;
      setIsLoading(true);
      setError(null);
      try {
        const usersRes = await userProfilesAPI.list({
          page: currentPage,
          page_size: currentSize,
        });
        const { items, totalCount: count } = parsePaginatedResponse(usersRes);
        setUsers(items);
        setTotalCount(count);
      } catch (err) {
        setError(err.message || t("referencePages.loadError"));
      } finally {
        setIsLoading(false);
      }
    },
    [page, pageSize, t]
  );

  useEffect(() => {
    loadDepartments();
  }, [loadDepartments]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const loadData = useCallback(async () => {
    await Promise.all([loadUsers(), loadDepartments()]);
  }, [loadUsers, loadDepartments]);

  const handlePageChange = (newPage) => {
    setPage(newPage);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize);
    setPage(1);
  };

  const getDeptIds = (profile) => {
    if (!profile.departments) return [];
    if (Array.isArray(profile.departments) && profile.departments.length > 0) {
      if (typeof profile.departments[0] === "object") {
        return profile.departments.map((d) => d.department_id);
      }
      return profile.departments;
    }
    return [];
  };

  const openAdd = () => {
    setSelectedUser(null);
    setInitialPermissionSnapshot(null);
    setFormData(emptyForm);
    setFormErrors({});
    setFormDialogOpen(true);
  };

  const openEdit = (profile) => {
    setSelectedUser(profile);
    setInitialPermissionSnapshot({
      can_edit_ticket: profile.can_edit_ticket || false,
      can_create_ticket: profile.can_create_ticket || false,
    });
    setFormData({
      username: profile.user?.username || "",
      password: "",
      email: profile.user?.email || "",
      first_name: profile.user?.first_name || "",
      last_name: profile.user?.last_name || "",
      arabic_name: profile.arabic_name || "",
      english_name: profile.english_name || "",
      job_title: profile.job_title || "",
      mobile: profile.mobile || "",
      departments: getDeptIds(profile),
      can_read_all_tickets: profile.can_read_all_tickets || false,
      can_create_ticket: profile.can_create_ticket || false,
      can_close_ticket: profile.can_close_ticket || false,
      can_manage_customers: profile.can_manage_customers || false,
      can_manage_contact_reasons: profile.can_manage_contact_reasons || false,
      can_manage_communication_methods:
        profile.can_manage_communication_methods || false,
      can_manage_short_subjects: profile.can_manage_short_subjects || false,
      can_manage_branches: profile.can_manage_branches || false,
      can_manage_products: profile.can_manage_products || false,
      can_manage_departments: profile.can_manage_departments || false,
      can_manage_users: profile.can_manage_users || false,
      can_view_reports: profile.can_view_reports || false,
      can_download_attachments: profile.can_download_attachments || false,
      can_reply_tickets: profile.can_reply_tickets || false,
      can_classify_tickets: profile.can_classify_tickets || false,
      can_edit_ticket: profile.can_edit_ticket || false,
      can_view_activity_log: profile.can_view_activity_log || false,
      can_delete_reply: profile.can_delete_reply || false,
      can_export_tickets: profile.can_export_tickets || false,
      can_manage_action_types: profile.can_manage_action_types || false,
      can_manage_employees: profile.can_manage_employees || false,
      can_add_reply_action: profile.can_add_reply_action || false,
      can_delete_reply_action: profile.can_delete_reply_action || false,
      can_view_actions_report: profile.can_view_actions_report || false,
      can_export_actions_report: profile.can_export_actions_report || false,
      is_active: profile.is_active !== false,
      receive_email: profile.receive_email !== false,
    });
    setFormErrors({});
    setFormDialogOpen(true);
  };

  const openDelete = (profile) => {
    setSelectedUser(profile);
    setDeleteDialogOpen(true);
  };

  const handleChange = (field) => (event) => {
    const value =
      event.target.type === "checkbox"
        ? event.target.checked
        : event.target.value;
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (formErrors[field]) {
      setFormErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!selectedUser && !formData.username.trim()) {
      errors.username = t("referencePages.fieldRequired");
    }
    if (!selectedUser && !formData.password.trim()) {
      errors.password = t("referencePages.fieldRequired");
    }
    if (!formData.arabic_name.trim()) {
      errors.arabic_name = t("referencePages.fieldRequired");
    }
    if (!formData.english_name.trim()) {
      errors.english_name = t("referencePages.fieldRequired");
    }
    if (!formData.job_title.trim()) {
      errors.job_title = t("referencePages.fieldRequired");
    }
    if (!formData.mobile.trim()) {
      errors.mobile = t("referencePages.fieldRequired");
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const buildPayload = () => {
    const payload = {
      email: formData.email.trim(),
      first_name: formData.first_name.trim(),
      last_name: formData.last_name.trim(),
      arabic_name: formData.arabic_name.trim(),
      english_name: formData.english_name.trim(),
      job_title: formData.job_title.trim(),
      mobile: formData.mobile.trim(),
      departments: formData.departments.map((id) => parseInt(id, 10)),
      can_read_all_tickets: formData.can_read_all_tickets,
      can_create_ticket: formData.can_create_ticket,
      can_close_ticket: formData.can_close_ticket,
      can_manage_customers: formData.can_manage_customers,
      can_manage_contact_reasons: formData.can_manage_contact_reasons,
      can_manage_communication_methods: formData.can_manage_communication_methods,
      can_manage_short_subjects: formData.can_manage_short_subjects,
      can_manage_branches: formData.can_manage_branches,
      can_manage_products: formData.can_manage_products,
      can_manage_departments: formData.can_manage_departments,
      can_manage_users: formData.can_manage_users,
      can_view_reports: formData.can_view_reports,
      can_download_attachments: formData.can_download_attachments,
      can_reply_tickets: formData.can_reply_tickets,
      can_classify_tickets: formData.can_classify_tickets,
      can_edit_ticket: formData.can_edit_ticket,
      can_view_activity_log: formData.can_view_activity_log,
      can_delete_reply: formData.can_delete_reply,
      can_export_tickets: formData.can_export_tickets,
      can_manage_action_types: formData.can_manage_action_types,
      can_manage_employees: formData.can_manage_employees,
      can_add_reply_action: formData.can_add_reply_action,
      can_delete_reply_action: formData.can_delete_reply_action,
      can_view_actions_report: formData.can_view_actions_report,
      can_export_actions_report: formData.can_export_actions_report,
      is_active: formData.is_active,
      receive_email: formData.receive_email,
    };
    if (!selectedUser) {
      payload.username = formData.username.trim();
      payload.password = formData.password;
    } else if (formData.password.trim()) {
      payload.password = formData.password;
    }
    return payload;
  };

  const shouldConfirmEditWithoutCreate = () => {
    if (!formData.can_edit_ticket || formData.can_create_ticket) {
      return false;
    }
    if (!selectedUser) {
      return true;
    }
    if (!initialPermissionSnapshot) {
      return true;
    }
    const editChanged =
      formData.can_edit_ticket !== initialPermissionSnapshot.can_edit_ticket;
    const createChanged =
      formData.can_create_ticket !== initialPermissionSnapshot.can_create_ticket;
    return editChanged || createChanged;
  };

  const saveUser = async () => {
    setIsSubmitting(true);
    setFormErrors({});
    try {
      const payload = buildPayload();
      if (selectedUser) {
        await userProfilesAPI.update(selectedUser.id, payload);
      } else {
        await userProfilesAPI.create(payload);
      }
      setFormDialogOpen(false);
      setEditWithoutCreateConfirmOpen(false);
      await loadData();
    } catch (err) {
      setFormErrors({ general: err.message });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSave = async () => {
    if (!validateForm()) return;
    if (shouldConfirmEditWithoutCreate()) {
      setEditWithoutCreateConfirmOpen(true);
      return;
    }
    await saveUser();
  };

  const handleConfirmDelete = async () => {
    if (!selectedUser) return;
    setIsSubmitting(true);
    setError(null);
    try {
      await userProfilesAPI.delete(selectedUser.id);
      setDeleteDialogOpen(false);
      setSelectedUser(null);
      await loadData();
    } catch (err) {
      setError(err.message);
      setDeleteDialogOpen(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getDeptLabel = (dept) =>
    isRTL ? dept.name_ar : dept.name_en;

  const filteredUsers = useMemo(() => {
    const term = tableFilter.trim().toLowerCase();
    if (!term) return users;

    const activeLabel = t("referencePages.active").toLowerCase();
    const inactiveLabel = t("referencePages.inactive").toLowerCase();

    return users.filter((profile) => {
      const searchable = [
        profile.user?.username,
        profile.arabic_name,
        profile.english_name,
        profile.job_title,
        profile.mobile,
        profile.is_active ? activeLabel : inactiveLabel,
      ];
      return searchable.some(
        (value) => value && String(value).toLowerCase().includes(term)
      );
    });
  }, [users, tableFilter, t]);

  if (!hasAccess) {
    return null;
  }

  return (
    <Box
      dir={isRTL ? "rtl" : "ltr"}
      sx={{ p: { xs: 0.5, sm: 1, md: 1.5 }, maxWidth: "100%", width: "100%" }}
    >
      <Paper elevation={3} sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
            flexDirection: { xs: "column", sm: "row" },
            gap: 2,
          }}
        >
          <Typography variant="h4" component="h1" sx={{ fontWeight: "bold" }}>
            {t("sidebar.users")}
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={openAdd}
            size="large"
          >
            {t("referencePages.addNew")}
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {!isLoading && users.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <TextField
              size="small"
              placeholder={t("referencePages.tableFilterPlaceholder")}
              value={tableFilter}
              onChange={(e) => setTableFilter(e.target.value)}
              InputProps={{
                startAdornment: (
                  <SearchIcon sx={{ mx: 1, color: "text.secondary" }} />
                ),
              }}
              sx={{ maxWidth: { xs: "100%", sm: 360 }, width: "100%" }}
            />
          </Box>
        )}

        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell align="left">
                    {t("referencePages.username")}
                  </TableCell>
                  <TableCell align="left">
                    {t("referencePages.arabicName")}
                  </TableCell>
                  <TableCell align="left">
                    {t("referencePages.englishName")}
                  </TableCell>
                  <TableCell align="left">
                    {t("referencePages.jobTitle")}
                  </TableCell>
                  <TableCell align="left">
                    {t("referencePages.mobile")}
                  </TableCell>
                  <TableCell align="center">
                    {t("common.status")}
                  </TableCell>
                  <TableCell align="center">
                    {t("common.actions")}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                      {t("referencePages.noRecords")}
                    </TableCell>
                  </TableRow>
                ) : filteredUsers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                      {t("referencePages.noFilterResults")}
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredUsers.map((profile) => (
                    <TableRow key={profile.id} hover>
                      <TableCell align="left">
                        {profile.user?.username}
                      </TableCell>
                      <TableCell align="left">
                        {profile.arabic_name}
                      </TableCell>
                      <TableCell align="left">
                        {profile.english_name}
                      </TableCell>
                      <TableCell align="left">
                        {profile.job_title}
                      </TableCell>
                      <TableCell align="left">
                        {profile.mobile}
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          size="small"
                          label={
                            profile.is_active
                              ? t("referencePages.active")
                              : t("referencePages.inactive")
                          }
                          color={profile.is_active ? "success" : "default"}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          color="primary"
                          onClick={() => openEdit(profile)}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          color="error"
                          onClick={() => openDelete(profile)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {!isLoading && (
          <ListPaginationBar
            page={page}
            pageSize={pageSize}
            totalCount={totalCount}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            isRTL={isRTL}
          />
        )}
      </Paper>

      <Dialog
        open={formDialogOpen}
        onClose={() => !isSubmitting && setFormDialogOpen(false)}
        maxWidth="md"
        fullWidth
        dir={isRTL ? "rtl" : "ltr"}
      >
        <DialogTitle>
          {selectedUser
            ? t("referencePages.editRecord")
            : t("referencePages.addNew")}
        </DialogTitle>
        <DialogContent>
          {formErrors.general && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {formErrors.general}
            </Alert>
          )}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
              gap: 2,
              pt: 1,
            }}
          >
            <TextField
              label={t("referencePages.username")}
              value={formData.username}
              onChange={handleChange("username")}
              error={Boolean(formErrors.username)}
              helperText={formErrors.username}
              required={!selectedUser}
              disabled={Boolean(selectedUser)}
              fullWidth
            />
            <TextField
              label={t("common.password")}
              type="password"
              value={formData.password}
              onChange={handleChange("password")}
              error={Boolean(formErrors.password)}
              helperText={
                formErrors.password ||
                (selectedUser ? t("referencePages.passwordOptional") : "")
              }
              required={!selectedUser}
              fullWidth
            />
            <TextField
              label={t("common.email")}
              value={formData.email}
              onChange={handleChange("email")}
              fullWidth
            />
            <TextField
              label={t("referencePages.firstName")}
              value={formData.first_name}
              onChange={handleChange("first_name")}
              fullWidth
            />
            <TextField
              label={t("referencePages.lastName")}
              value={formData.last_name}
              onChange={handleChange("last_name")}
              fullWidth
            />
            <TextField
              label={t("referencePages.arabicName")}
              value={formData.arabic_name}
              onChange={handleChange("arabic_name")}
              error={Boolean(formErrors.arabic_name)}
              helperText={formErrors.arabic_name}
              required
              fullWidth
            />
            <TextField
              label={t("referencePages.englishName")}
              value={formData.english_name}
              onChange={handleChange("english_name")}
              error={Boolean(formErrors.english_name)}
              helperText={formErrors.english_name}
              required
              fullWidth
            />
            <TextField
              label={t("referencePages.jobTitle")}
              value={formData.job_title}
              onChange={handleChange("job_title")}
              error={Boolean(formErrors.job_title)}
              helperText={formErrors.job_title}
              required
              fullWidth
            />
            <TextField
              label={t("referencePages.mobile")}
              value={formData.mobile}
              onChange={handleChange("mobile")}
              error={Boolean(formErrors.mobile)}
              helperText={formErrors.mobile}
              required
              fullWidth
            />
            <FormControl fullWidth sx={{ gridColumn: { sm: "1 / -1" } }}>
              <InputLabel>{t("createTicket.departmentsLabel")}</InputLabel>
              <Select
                multiple
                value={formData.departments}
                onChange={handleChange("departments")}
                input={<OutlinedInput label={t("createTicket.departmentsLabel")} />}
                renderValue={(selected) => (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {selected.map((id) => {
                      const dept = departments.find(
                        (d) => d.department_id === id
                      );
                      return (
                        <Chip
                          key={id}
                          label={dept ? getDeptLabel(dept) : id}
                          size="small"
                        />
                      );
                    })}
                  </Box>
                )}
              >
                {departments.map((dept) => (
                  <MenuItem key={dept.department_id} value={dept.department_id}>
                    {getDeptLabel(dept)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.can_read_all_tickets}
                  onChange={handleChange("can_read_all_tickets")}
                />
              }
              label={t("referencePages.canReadAllTickets")}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.is_active}
                  onChange={handleChange("is_active")}
                />
              }
              label={t("referencePages.active")}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.receive_email}
                  onChange={handleChange("receive_email")}
                />
              }
              label={t("referencePages.receiveEmail")}
            />
            <Typography
              variant="subtitle1"
              sx={{ gridColumn: "1 / -1", fontWeight: "bold", mt: 1 }}
            >
              {t("referencePages.permissionsTitle")}
            </Typography>
            {PERMISSION_FIELDS.map(({ key, labelKey }) => (
              <FormControlLabel
                key={key}
                control={
                  <Checkbox
                    checked={formData[key]}
                    onChange={handleChange(key)}
                  />
                }
                label={t(labelKey)}
              />
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setFormDialogOpen(false)}
            disabled={isSubmitting}
          >
            {t("common.cancel")}
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={isSubmitting}
          >
            {isSubmitting ? <CircularProgress size={24} /> : t("common.save")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={editWithoutCreateConfirmOpen}
        onClose={() => setEditWithoutCreateConfirmOpen(false)}
        dir={isRTL ? "rtl" : "ltr"}
      >
        <DialogTitle>{t("referencePages.confirmEditWithoutCreateTitle")}</DialogTitle>
        <DialogContent>
          <Typography>{t("referencePages.confirmEditWithoutCreate")}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditWithoutCreateConfirmOpen(false)}>
            {t("referencePages.no")}
          </Button>
          <Button
            onClick={saveUser}
            variant="contained"
            disabled={isSubmitting}
          >
            {t("referencePages.yes")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        dir={isRTL ? "rtl" : "ltr"}
      >
        <DialogTitle>{t("referencePages.confirmDelete")}</DialogTitle>
        <DialogContent>
          <Typography>
            {t("referencePages.deleteConfirmMessage", {
              name: selectedUser?.user?.username || "",
            })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>
            {t("common.cancel")}
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
            disabled={isSubmitting}
          >
            {t("common.delete")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UsersPage;
